import type { EditionDefinition } from '@/editions/types';

/**
 * Fase 1's Fit edition had one section, Home, with Departments and Users as
 * its subitems. `PLAN-FASE1.md` (§ Hoofdmenu) sketched a later three-section
 * shape (Home/Insights/Programs, see PLAN-FASE2.md § Navigatiemodel) — Fase
 * 2 WP-02 adds the second pill, **Insights**, with its first subitem,
 * **Vitality** (`/vitality`, `src/features/vitality/`). WP-03 adds
 * Insights' other subitem, **Participation** (`/participation`,
 * `src/features/participation/`), the engagement funnel. WP-04 adds the
 * third pill, **Programs**, with its first subitem, **Pilots**
 * (`/pilots`, `src/features/pilots/`); WP-05 adds Programs' other subitem,
 * **Challenges** (`/challenges`, `src/features/challenges/`) — same "each
 * WP adds only its own entry" rule earlier work packages used. WP-06 adds
 * **Overview** (`/overview`, `src/features/overview/`) as Home's *first*
 * subitem (§ Navigatiemodel: "Overview schuift van eigen pill naar eerste
 * subitem van Home"), so Home's `to` now points at Overview instead of
 * Departments.
 *
 * PLAN-FASE3.md WP-02 adds the 4th pill, **Content** (§ Navigatiemodel:
 * Communicatie/Documenten/Rapportages all live here, a new domain
 * unrelated to Fase 2's Vitality/Participation/Pilots/Challenges), with
 * its first subitem, **Communicatie** (`/communications`,
 * `src/features/communications/`). WP-03 adds Content's second subitem,
 * **Documenten** (`/documents`, `src/features/documents/`). WP-04 adds
 * Content's third and final subitem, **Rapportages** (`/reports`,
 * `src/features/reports/`) — same "each WP adds only its own entry" rule
 * as Insights/Programs above.
 *
 * The redesign (§ Hoofdmenu, latest pass) drops the four grouped pills for
 * a single flat list, in this fixed order: Overzicht, Vitaliteit, Deelname,
 * Pilot, Challenges, Communicatie, Documenten, Medewerkers, Rapportages,
 * Instellingen — with Afdelingen, which isn't part of that list, pushed to
 * the very bottom. Each entry below is still modelled as a `Section` with
 * exactly one `Item` (rather than reshaping `EditionDefinition` itself),
 * so permission/module filtering in `useVisibleSections` and the
 * edition-route gate in `routes.tsx` (both keyed off `section.match`)
 * don't need to change; `Sidebar` renders a section with one item as a
 * plain flat row instead of a headed group. **Instellingen** is new here —
 * it used to only be reachable from the account menu (`AccountMenu`);
 * `canSeeSettingsSection` mirrors the account menu's own gate
 * (`canViewCompanyInternals`), loosened to not need a specific company id
 * since the sidebar has no single active-company parameter to check
 * against.
 */
export const fit: EditionDefinition = {
  id: 'fit',
  brandSuffix: 'FIT',
  sections: [
    {
      id: 'overview',
      labelKey: 'editions.fit.items.overview',
      to: '/overview',
      match: ['/overview'],
      visible: (p) => p.canSeeDepartmentsSection || p.canSeeUsersSection,
      items: [
        {
          id: 'overview',
          labelKey: 'editions.fit.items.overview',
          to: '/overview',
          visible: (p) => p.canSeeDepartmentsSection || p.canSeeUsersSection,
        },
      ],
    },
    {
      id: 'vitality',
      labelKey: 'editions.fit.items.vitality',
      to: '/vitality',
      match: ['/vitality'],
      visible: (p) => p.canSeeDepartmentsSection,
      items: [
        {
          id: 'vitality',
          labelKey: 'editions.fit.items.vitality',
          to: '/vitality',
          visible: (p) => p.canSeeDepartmentsSection,
        },
      ],
    },
    {
      id: 'participation',
      labelKey: 'editions.fit.items.participation',
      to: '/participation',
      match: ['/participation'],
      visible: (p) => p.canSeeDepartmentsSection,
      items: [
        {
          id: 'participation',
          labelKey: 'editions.fit.items.participation',
          to: '/participation',
          visible: (p) => p.canSeeDepartmentsSection,
        },
      ],
    },
    {
      id: 'pilots',
      labelKey: 'editions.fit.items.pilots',
      to: '/pilots',
      match: ['/pilots'],
      visible: (p) => p.canSeeDepartmentsSection,
      items: [
        {
          id: 'pilots',
          labelKey: 'editions.fit.items.pilots',
          to: '/pilots',
          visible: (p) => p.canSeeDepartmentsSection,
        },
      ],
    },
    {
      id: 'challenges',
      labelKey: 'editions.fit.items.challenges',
      to: '/challenges',
      match: ['/challenges'],
      visible: (p) => p.canSeeChallengesSection,
      items: [
        {
          id: 'challenges',
          labelKey: 'editions.fit.items.challenges',
          to: '/challenges',
          visible: (p) => p.canSeeChallengesSection,
        },
      ],
    },
    {
      id: 'communications',
      labelKey: 'editions.fit.items.communications',
      to: '/communications',
      match: ['/communications'],
      visible: (p) => p.canSeeCommunicationsSection,
      items: [
        {
          id: 'communications',
          labelKey: 'editions.fit.items.communications',
          to: '/communications',
          visible: (p) => p.canSeeCommunicationsSection,
        },
      ],
    },
    {
      id: 'documents',
      labelKey: 'editions.fit.items.documents',
      to: '/documents',
      match: ['/documents'],
      visible: (p) => p.canSeeDocumentsSection,
      items: [
        {
          id: 'documents',
          labelKey: 'editions.fit.items.documents',
          to: '/documents',
          visible: (p) => p.canSeeDocumentsSection,
        },
      ],
    },
    {
      id: 'users',
      labelKey: 'editions.fit.items.users',
      to: '/users',
      match: ['/users'],
      visible: (p) => p.canSeeUsersSection,
      items: [
        {
          id: 'users',
          labelKey: 'editions.fit.items.users',
          to: '/users',
          visible: (p) => p.canSeeUsersSection,
        },
      ],
    },
    {
      id: 'reports',
      labelKey: 'editions.fit.items.reports',
      to: '/reports',
      match: ['/reports'],
      visible: (p) => p.canSeeReportsSection,
      items: [
        {
          id: 'reports',
          labelKey: 'editions.fit.items.reports',
          to: '/reports',
          visible: (p) => p.canSeeReportsSection,
        },
      ],
    },
    {
      id: 'settings',
      labelKey: 'editions.fit.items.settings',
      to: '/settings',
      match: ['/settings'],
      visible: (p) => p.canSeeSettingsSection,
      items: [
        {
          id: 'settings',
          labelKey: 'editions.fit.items.settings',
          to: '/settings',
          visible: (p) => p.canSeeSettingsSection,
        },
      ],
    },
    // Not part of the redesigned flat list — kept as a working entry point
    // to `/departments`, pushed to the bottom (§ Hoofdmenu).
    {
      id: 'departments',
      labelKey: 'editions.fit.items.departments',
      to: '/departments',
      match: ['/departments'],
      visible: (p) => p.canSeeDepartmentsSection,
      items: [
        {
          id: 'departments',
          labelKey: 'editions.fit.items.departments',
          to: '/departments',
          visible: (p) => p.canSeeDepartmentsSection,
        },
      ],
    },
  ],
};
